import PropTypes from 'prop-types';
import styled from 'styled-components';

import { theme } from '_constants/theme';
import { ICONS_ALIASES } from '_constants/icons';

import PriceBanner from '_components/atomic/PriceBanner';
import Icon from '_components/atomic/Icon';

const { colors, devices, fontSizes, spaces } = theme;

const StyledPriceSection = styled.div`
  max-width: ${spaces.large24X * 10 + 80}px;
  width: 100%;
  margin-bottom: ${spaces.large24X}px;

  @media ${devices.tablet} {
    margin-left: 0;
    margin-right: 0;
    margin-bottom: ${spaces.large9X}px;
  }
`;

const StyledPriceBannerBadgeTextContainer = styled.div`
  padding-top: ${spaces.large}px;
  padding-left: ${spaces.large9X}px;
  padding-right: ${spaces.large9X}px;
  font-family: 'Aeonik';
  font-style: normal;
  font-weight: 700;
  font-size: ${fontSizes.large12X + 2}px;
  line-height: 130%;
  color: ${colors.white};
`;

const StyledPriceBannerBadgeSubtextContainer = styled.div`
  padding-top: ${spaces.small2X}px;
  padding-left: ${spaces.large7X}px;
  padding-right: ${spaces.large7X}px;
  padding-bottom: ${spaces.large3X}px;
  font-family: 'Aeonik';
  font-style: normal;
  font-weight: 500;
  font-size: ${fontSizes.large}px;
  line-height: 150%;
  color: ${colors.white};
  text-align: center;
`;

const StyledPriceBannerTitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: ${spaces.large6X}px;
  font-family: 'Aeonik';
  font-style: normal;
  font-weight: 500;
  font-size: ${fontSizes.large11X}px;
  line-height: 130%;
  color: ${colors.white};

  span {
    display: block;
    width: 100%;
  }

  span:last-child {
    margin-top: ${spaces.smallX}px;
    font-size: ${fontSizes.regular}px;
    line-height: 150%;
  }

  @media ${devices.tablet} {
    margin-top: ${spaces.regular}px;
    margin-bottom: ${spaces.large2X}px;
    text-align: center;
    font-size: ${fontSizes.large2X}px;
  }
`;

const StyledPriceBannerBodyContainer = styled.div`
  @media ${devices.tablet} {
    margin-bottom: ${spaces.smallX}px;
  }
`;

const StyledBullet = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${spaces.small - 2}px;

  &:last-child {
    margin-bottom: ${spaces.large2X}px;
  }
`;

const StyledBulletIconContainer = styled.div`
  margin-right: ${spaces.small}px;
`;

const StyledBulletText = styled.div`
  flex-grow: 1;
  font-family: 'Aeonik';
  font-style: normal;
  font-size: ${fontSizes.large}px;
  font-weight: 400;
  color: ${colors.white};

  @media ${devices.tablet} {
    font-size: ${fontSizes.regular}px;
  }
`;
const StyledUnderBulletsText = styled.div`
  margin-top: ${spaces.large4X}px;
  flex-grow: 1;
  font-family: 'Aeonik';
  font-style: normal;
  font-size: ${fontSizes.regular}px;
  font-weight: 300;
  color: ${colors.white};
  font-style: italic;
  @media ${devices.tablet} {
    margin-top: ${spaces.large2X}px;
  }
`;

const PriceSection = ({
  title,
  subtitle,
  price,
  priceText,
  bullets,
  underBulletsText,
  buttons,
}) => {
  return (
    <StyledPriceSection>
      <PriceBanner
        notRoundedMobile
        innerSpace={spaces.large9X}
        roundingRadius={40}
        footer={
          <StyledUnderBulletsText>{underBulletsText}</StyledUnderBulletsText>
        }
        badgeRoundingRadius={30}
        color={{
          gradient: {
            from: colors.blue[600],
            to: colors.blue[500],
          },
        }}
        title={
          <StyledPriceBannerTitleContainer>
            <span>{title}</span>
            <span>{subtitle}</span>
          </StyledPriceBannerTitleContainer>
        }
        badgeText={
          <StyledPriceBannerBadgeTextContainer>
            {`$${price?.toFixed(2) || '0.00'}`}
          </StyledPriceBannerBadgeTextContainer>
        }
        badgeSubtext={
          <StyledPriceBannerBadgeSubtextContainer>
            {priceText}
          </StyledPriceBannerBadgeSubtextContainer>
        }
        buttons={buttons}
      >
        <StyledPriceBannerBodyContainer>
          {bullets?.map((bullet, idx) => (
            <StyledBullet key={`bullet:${idx}`}>
              <StyledBulletIconContainer>
                <Icon alias={ICONS_ALIASES.checkRounded} color={colors.white} />
              </StyledBulletIconContainer>
              <StyledBulletText>{bullet}</StyledBulletText>
            </StyledBullet>
          ))}
        </StyledPriceBannerBodyContainer>
      </PriceBanner>
    </StyledPriceSection>
  );
};

PriceSection.propTypes = {
  title: PropTypes.node,
  subtitle: PropTypes.node,
  price: PropTypes.number,
  priceText: PropTypes.node,
  bullets: PropTypes.arrayOf(PropTypes.node),
  buttons: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.node,
      color: PropTypes.string,
      disabled: PropTypes.bool,
      large: PropTypes.bool,
      transparent: PropTypes.bool,
      outlined: PropTypes.bool,
      prefix: PropTypes.node,
      suffix: PropTypes.node,
      secondary: PropTypes.bool,
      onClick: PropTypes.func,
    })
  ),
};

export default PriceSection;
